import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import SocialLogin from '../components/SocialLogin';

function LoginScreen() {
  const token = localStorage.getItem('aircheck_access');
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  const [isSignup, setIsSignup] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token) navigate('/');
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const url = isSignup
        ? `${API_BASE_URL}/api/v1/signup/`
        : `${API_BASE_URL}/api/token/`;
      const data = isSignup ? { username, email, password } : { username, password };
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (!isSignup) {
          localStorage.setItem('aircheck_access', responseData.access);
          localStorage.setItem('aircheck_refresh', responseData.refresh);
          navigate('/');
        } else {
          setIsSignup(false);
        }
      } else {
        let message = isSignup ? 'Signup failed' : 'Authentication failed';
        try {
          const data = await response.json();
          // 429 = rate-limited or temporarily locked out after repeated failures.
          if (response.status === 429) {
            message = data.detail || 'Too many attempts. Please wait a moment and try again.';
          } else if (data.detail) {
            message = data.detail;
          }
        } catch (_) {
          // non-JSON error body; keep the default message
        }
        setError(message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '85vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: (t) =>
            t.palette.mode === 'dark'
              ? 'radial-gradient(60% 60% at 80% 20%, rgba(229,9,20,0.18) 0%, transparent 60%), radial-gradient(60% 60% at 20% 80%, rgba(0,183,255,0.10) 0%, transparent 60%)'
              : 'radial-gradient(60% 60% at 80% 20%, rgba(229,9,20,0.10) 0%, transparent 60%)',
        }}
      />
      <Container maxWidth="sm" sx={{ position: 'relative' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4.5 },
            borderRadius: 4,
            border: (t) => `1px solid ${t.palette.divider}`,
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, textAlign: 'center' }}>
            {isSignup ? 'Create your account' : 'Welcome back'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            {isSignup
              ? 'Track shows, set reminders, never miss an episode.'
              : 'Sign in to access your watchlist and reminders.'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <SocialLogin isSignup={isSignup} />
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">
              or continue with email
            </Typography>
          </Divider>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                required
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                size="medium"
              />
              {isSignup && (
                <TextField
                  required
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  size="medium"
                />
              )}
              <TextField
                required
                type={showPassword ? 'text' : 'password'}
                label="Password"
                value={password}
                autoComplete="off"
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                size="medium"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={submitting}
                fullWidth
              >
                {submitting ? 'Please wait…' : isSignup ? 'Create account' : 'Sign in'}
              </Button>
            </Stack>
          </Box>

          <Typography sx={{ mt: 3, textAlign: 'center' }} variant="body2" color="text.secondary">
            {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
            <Link
              component={RouterLink}
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setIsSignup((s) => !s);
              }}
              underline="hover"
              sx={{ color: 'primary.main', fontWeight: 600 }}
            >
              {isSignup ? 'Sign in' : 'Sign up'}
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default LoginScreen;
